/**
 * Company Settings API Client
 * 회사 설정 관련 API 호출
 */

import { apiClient } from '@/shared/api/client'

// ========================
// Type Definitions
// ========================

export interface BasicInfoData {
  companyName: string
  registrationNumber: string
  jurisdiction: string
  countryCode: string
  countryName: string
  joinDate: string
  hasLogo: boolean
  ownerUserId: string | null
  ownerEmail: string
  ownerFirstName: string
  ownerMiddleName: string
  ownerLastName: string
}

export interface SubscriptionData {
  subscriptionId: number
  planCd: string
  planKey: string
  planName: string
  isTrial: boolean
  billingPeriod: string
  status: string
  startDate: string
  endDate: string | null
  renewalDate: string | null
  entityCount: number
  isAutoRenew: boolean
  trialDaysRemaining: number | null
}

export interface BillingInfoData {
  billingEmail: string
  paymentMethod: string
  cardLastFour: string
  cardBrand: string
  cardExpiryMonth: number | null
  cardExpiryYear: number | null
  hasConsent?: boolean
  postalCode: string
  blockHouseNo: string
  streetName: string
  buildingName: string
  addrLevel: string
  addrUnit: string
  billingCountry: string
  state: string
  city: string
}

export interface BillingData {
  subscription: SubscriptionData | null
  billingInfo: BillingInfoData | null
  entityUsage: {
    activeCount: number
    limit: number
  }
  jurisdiction: string | null
}

export interface PlanPricing {
  pricingId: number
  jurisdiction: string
  currency: string
  priceMonthly: number
  priceYearly: number
  price3Yearly: number
  price5Yearly: number
  entityAddonPriceMonthly: number | null
  entityAddonPriceYearly: number | null
  entityAddonPrice3Yearly: number | null
  entityAddonPrice5Yearly: number | null
  taxRate: number
  taxLabel: string | null
}

export interface PlanData {
  planCd: string
  planKey: string
  planName: string
  planDescription: string
  planLevel: number
  minEntityFirst: number
  minEntityAddon: number
  trialDays: number | null
  isTrial: boolean
  maxUsers: number | null
  pricing: PlanPricing | null
}

export interface BillingHistoryItem {
  paymentId?: number
  paymentType: string
  status: string
  amount: number
  currency: string
  product: string
  billingPeriod: string
  entityQty: number | null
  unitPrice: number | null
  prorationDays: number | null
  prorationTotalDays: number | null
  requestDate: string
  confirmedDate: string | null
  // 결제 외 문서(관리자 수동 인보이스 / 견적)도 이력에 노출 — kind 로 구분, invoiceId 로 다운로드
  kind?: 'INVOICE' | 'QUOTE' | 'MARKETPLACE'
  invoiceId?: number
  marketplaceBillingId?: string
}

export interface MemberData {
  userId: string
  email: string
  firstName: string
  middleName: string
  lastName: string
  name: string
  positionCode: string
  roleName: string
  departmentCode: string
  departmentName: string
  isOwner: boolean
  isFirstSignUpUser: boolean
}

export interface EntityData {
  entityCd: string
  companyName: string
  regNo: string
  regNoOld: string
  dateOfIncorporation: string | null
  isActive: boolean
  /** 비활성화한 시각. 예전에 비활성화된 건은 값이 없다. */
  deactivatedDate?: string | null
  /** 이 시각 이후부터 다시 활성화할 수 있다. null 이면 제약 없음. */
  reactivatableAt?: string | null
}

export interface SubscriptionInfoData {
  currentPlan: string
  planCd: string
  baseEntities: number
  existingAdditional: number
  entityUsage: number
  trialDaysRemaining: number | null
  billingPeriod: string
  billingEmail: string
}

export interface AddonCalculation {
  addEntities: number
  unitPrice: number
  currency: string
  billingPeriod: string
  remainingDays: number
  totalDays: number
  amountBeforeFloor: number
  amount: number
  description: string
}

// ========================
// API Functions
// ========================

export const companySettingsApi = {
  // ── Basic Info ──
  getBasicInfo: () =>
    apiClient.get<{ data: BasicInfoData }>('/company-settings/basic-info'),

  getLogo: () =>
    apiClient.get<{ data: { logo: string } }>('/company-settings/basic-info/logo'),

  uploadLogo: (logo: string) =>
    apiClient.put<{ message: string }>('/company-settings/basic-info/logo', { logo }),

  deleteLogo: () =>
    apiClient.delete<{ message: string }>('/company-settings/basic-info/logo'),

  changeOwner: (newOwnerUserId: string) =>
    apiClient.post<{ message: string }>('/company-settings/basic-info/change-owner', { newOwnerUserId }),

  // ── Billing ──
  getBilling: () =>
    apiClient.get<BillingData>('/company-settings/billing'),

  getSubscriptionInfo: () =>
    apiClient.get<{ data: SubscriptionInfoData }>('/company-settings/subscription-info'),

  updateBillingInfo: (data: Partial<BillingInfoData>) =>
    apiClient.put<{ message: string }>('/company-settings/billing-info', data),

  getBillingHistory: (year?: number) =>
    apiClient.get<{ history: BillingHistoryItem[]; years: number[] }>(
      '/company-settings/billing-history',
      { params: { year } }
    ),

  searchPostalCode: (postalCode: string) =>
    apiClient.get<{ data: { blockHouseNo: string; streetName: string; buildingEstateName: string } }>(
      '/company-settings/search-postal-code',
      { params: { postalCode } }
    ),

  getPlans: () =>
    apiClient.get<{ plans: PlanData[]; clientJurisdiction: string }>('/company-settings/plans'),

  calculateAddon: (addEntities: number, billingPeriod?: string) =>
    apiClient.post<{ data: AddonCalculation }>('/company-settings/calculate-addon', { addEntities, billingPeriod }),

  calculateUpgrade: (newPlanCd: string, newEntityQty: number, newBillingPeriod?: string) =>
    apiClient.post<{
      isNewSubscription: boolean
      oldUnitPrice: number
      newUnitPrice: number
      oldEntityCount: number
      newEntityQty: number
      remainingDays: number
      totalDays: number
      credit: number
      newCost: number
      upgradeAmount: number
      upgradeAmountBeforeFloor: number
      currency: string
      billingPeriod: string
      description: string
    }>('/company-settings/calculate-upgrade', { newPlanCd, newEntityQty, newBillingPeriod }),

  // ── Payment ──
  createPaymentIntent: (data: {
    planCd: string
    billingPeriod: string
    entityQty: number
    paymentType: string
    amount: number
    amountBeforeFloor?: number
    currency: string
    unitPrice: number
    prorationDays?: number
    prorationTotalDays?: number
    productDescription: string
    billingEmail?: string
    promoCode?: string
    skipPromo?: boolean
  }) =>
    apiClient.post<{
      paymentId: number; intentId: string; clientSecret: string; amount: number; currency: string
      appliedPromo?: { code: string; discountRate: number } | null
      promoSkippedReason?: string | null
    }>(
      '/company-settings/payment-intent',
      data
    ),

  verifyPayment: (paymentId: number, intentId: string, status: string) =>
    apiClient.post<{ paymentId: number; status: string }>('/company-settings/payment-verify', {
      paymentId,
      intentId,
      status
    }),

  // 저장된 카드(consent)로 재입력 없이 결제 → { paymentId, intentId, status } (이후 verifyPayment로 후처리)
  // 저장카드 없음/만료 시 409 (code: NO_SAVED_CARD | CARD_EXPIRED) → 프론트가 카드입력으로 폴백
  payWithSavedCard: (data: {
    planCd: string
    billingPeriod: string
    entityQty: number
    paymentType: string
    amount: number
    amountBeforeFloor?: number
    currency: string
    unitPrice: number
    prorationDays?: number
    prorationTotalDays?: number
    productDescription: string
    promoCode?: string
    skipPromo?: boolean
  }) =>
    apiClient.post<{
      paymentId: number; intentId: string; status: string; cardLastFour?: string
      appliedPromo?: { code: string; discountRate: number } | null
      promoSkippedReason?: string | null
    }>(
      '/company-settings/pay-saved-card',
      data
    ),

  // ── Promo Code (§5.2) ──
  // Type A 잠금 코드 자동적용 조회. 없으면 data:null.
  getApplicablePromo: (paymentType: string) =>
    apiClient.get<{ code: string; discountRate: number; phase: 'FIRST' | 'REUSE'; deadline: string | null } | null>(
      `/company-settings/promo/applicable?paymentType=${encodeURIComponent(paymentType)}`
    ),

  // BillingTab 상태 카드용 — 연결된 코드를 만료 여부와 무관하게 국면과 함께 반환.
  getMyPromo: () =>
    apiClient.get<{
      code: string; discountRate: number; promoType: string
      // NOT_YET: 체험 고객에게 지정된 Type B — 유료 전환 후 갱신·증설부터 사용 가능
      phase: 'FIRST_PAYMENT' | 'REUSE' | 'NOT_YET' | 'EXPIRED'; deadline: string | null; expiredReason?: string
    } | null>('/company-settings/promo/mine'),

  // Type B(지정사) 수동 입력 검증. 실패 시 axios가 400을 throw하므로 호출측에서 catch.
  validatePromoCode: (code: string, paymentType: string, amount: number) =>
    apiClient.post<{
      valid: boolean; code?: string; discountRate?: number; discountAmount?: number; finalAmount?: number; reason?: string
    }>('/company-settings/promo/validate', { code, paymentType, amount }),

  // ── Receipt ──
  downloadReceipt: (paymentId: number) =>
    apiClient.getInstance().get(`/company-settings/receipt/${paymentId}`, { responseType: 'blob' }),

  // ── Invoice + Receipt 통합 다운로드 ──
  downloadBillingDocument: (paymentId: number) =>
    apiClient.getInstance().get(`/company-settings/billing-document/${paymentId}`, { responseType: 'blob' }),

  // ── 개별 인보이스(관리자 수동 발급 등) 다운로드 ──
  downloadInvoice: (invoiceId: number) =>
    apiClient.getInstance().get(`/company-settings/invoice/${invoiceId}`, { responseType: 'blob' }),

  // ── Quote (PDF) — payload로 결제화면 선택값 전달 가능 ──
  downloadQuote: (payload: Record<string, unknown> = {}) =>
    apiClient.getInstance().post('/company-settings/request-quote', payload, { responseType: 'blob' }),

  // 1/3/5년 비교 견적(트라이얼 "Request Quote" 1클릭용) — 한 PDF에 세 기간 나란히 비교.
  downloadQuoteComparison: (payload: Record<string, unknown> = {}) =>
    apiClient.getInstance().post('/company-settings/request-quote-comparison', payload, { responseType: 'blob' }),

  // ── Members ──
  getMembers: () =>
    apiClient.get<MemberData[]>('/company-settings/members'),

  getAvailableMembers: () =>
    apiClient.get<MemberData[]>('/company-settings/members/available'),

  inviteMembers: (userIds: string[], positionCode?: string) =>
    apiClient.post<{ message: string }>('/company-settings/members/invite', { userIds, positionCode }),

  getDepartments: () =>
    apiClient.get<{ departmentCode: string; departmentName: string }[]>('/company-settings/members/departments'),

  updateMemberDepartment: (userId: string, departmentCode: string) =>
    apiClient.put<{ message: string }>(`/company-settings/members/${userId}`, { departmentCode }),

  removeMember: (userId: string) =>
    apiClient.delete<{ message: string }>(`/company-settings/members/${userId}`),

  // ── Entities ──
  getEntities: () =>
    apiClient.get<{ entities: EntityData[]; activeCount: number; entityLimit: number; peakEntityCount: number | null; totalCount: number }>(
      '/company-settings/entities'
    ),

  toggleEntity: (entityCd: string, isActive: boolean) =>
    apiClient.patch<{ message: string }>(`/company-settings/entities/${entityCd}/toggle`, { isActive }),

  // ── Subscription Management ──
  toggleAutoRenew: (isAutoRenew: boolean) =>
    apiClient.patch<{ isAutoRenew: boolean }>('/company-settings/auto-renew', { isAutoRenew }),

  // 엔티티 한도 감소 (결제 없음 — 다음 갱신부터 감소 금액. 현재 active 엔티티 수 미만 불가)
  decreaseEntityLimit: (newLimit: number) =>
    apiClient.post<{ success: boolean; message?: string; data?: { entityCount: number } }>(
      '/company-settings/entity-decrease', { newLimit }
    ),

  createCardUpdateIntent: () =>
    apiClient.post<{ clientSecret: string; customerId: string; currency: string }>('/company-settings/update-card'),

  verifyCardUpdate: (data: {
    consentId: string;
    paymentMethodId: string;
    cardLastFour?: string | null;
    cardBrand?: string | null;
    cardExpiryMonth?: number | null;
    cardExpiryYear?: number | null;
  }) =>
    apiClient.post<{ cardLastFour: string; cardBrand: string; cardExpiryMonth: number; cardExpiryYear: number }>(
      '/company-settings/update-card-verify',
      data
    ),

  getEmailHistory: () =>
    apiClient.get<{
      emails: {
        logId: number
        sendDate: string
        status: string
        type: string
        subject: string
      }[]
    }>('/company-settings/email-history'),

  getConsentStatus: () =>
    apiClient.get<{
      hasConsent: boolean
      consentId: string | null
      consentStatus: string | null
      customerId: string | null
      message?: string
      airwallex?: {
        status: string
        nextTriggeredBy: string
        merchantTriggerReason: string
        createdAt: string
        updatedAt: string
        customerId: string
        paymentMethodId: string
        currency: string
      }
    }>('/company-settings/consent-status'),
}
